// TapTap's curated routine catalogue. These definitions are product content;
// user progress is stored separately so catalogue updates do not rewrite data.
export const ROUTINES = Object.freeze([
  {
    id: 'moment-of-calm',
    title: 'A Moment of Calm',
    durationMinutes: 3,
    description: 'A slow, steady visual rhythm for making a little space in your day.',
    openingPrompt: 'Settle comfortably and let your gaze rest on the glow.',
    paceLabel: 'Slow and spacious',
    visualLabel: 'TapTap blue and purple · large, gentle orbs',
    defaultMode: 'watch',
    watchOnly: true,
    settings: {
      orbColor: 'blue', orbSize: 4.2, orbSpacing: 48,
      reappearSpeed: 2.6, fadeDurationMs: 1600,
      audioVolume: 20, kidsMode: false,
    },
    prompts: [
      'Let your gaze rest on the glow.',
      'If it feels comfortable, notice an easy breath. There is no need to change it.',
      'Notice one thing you can see and one thing you can feel.',
    ],
  },
  {
    id: 'before-sleep',
    title: 'Before Sleep',
    durationMinutes: 5,
    description: 'A dusk-like rhythm for easing out of the day, without trying to force sleep.',
    openingPrompt: 'Lower the brightness, get comfortable and follow the slower rhythm at your own pace.',
    paceLabel: 'Very slow and unhurried',
    visualLabel: 'Large sunset orbs settling from gold into night',
    defaultMode: 'watch',
    watchOnly: true,
    hideCountdown: true,
    completionHaptics: false,
    prompts: [
      'Let your eyes rest on the fading light.',
      'There is nothing you need to achieve. Follow only while it feels comfortable.',
      'Rest may come now or later. Let the final glow fade in its own time.',
    ],
    settings: {
      orbColor: 'sleepDusk', orbSize: 4.8, orbSpacing: 58,
      reappearSpeed: 5, fadeDurationMs: 4500,
      audioEnabled: false, audioVolume: 0, kidsMode: false,
    },
  },
  {
    id: 'feeling-overwhelmed',
    title: 'Feeling Overwhelmed',
    durationMinutes: 2,
    description: 'A short, predictable visual anchor when there is too much competing for attention.',
    openingPrompt: 'You do not need to solve everything in this moment. Feel the surface supporting you.',
    paceLabel: 'Grounded and predictable',
    visualLabel: 'Stable teal · larger orbs',
    prompts: [
      'Feel the surface supporting you.',
      'Notice three things you can see, then two things you can hear.',
      'Notice one point of contact — your feet, hands or seat. You can stop or continue.',
    ],
    settings: {
      orbColor: 'teal', orbSize: 4, orbSpacing: 42,
      reappearSpeed: 1.8, fadeDurationMs: 1000,
      audioVolume: 20, kidsMode: false,
    },
  },
  {
    id: 'wake-up',
    title: 'Wake Up',
    durationMinutes: 4,
    description: 'A gradually brightening rhythm to ease your attention into the day.',
    openingPrompt: 'Begin with the darker glow and let the colours brighten as you wake.',
    paceLabel: 'Steady and gently energising',
    visualLabel: 'Sunrise red to orange and yellow',
    prompts: [
      'Take an easy breath and notice the first warm glow.',
      'Let the brighter rhythm bring your attention into the room.',
      'Notice the daylight colours and choose how you want to begin.',
    ],
    settings: {
      orbColor: 'sunrise', orbSize: 3.6, orbSpacing: 54,
      reappearSpeed: 2.2, fadeDurationMs: 1000,
      paceJourney: {
        startReappearSpeed: 2.2,
        endReappearSpeed: 1.4,
        startFadeDurationMs: 1000,
        endFadeDurationMs: 600,
      },
      audioVolume: 40, kidsMode: false,
    },
  },
  {
    id: 'refocus-and-reset',
    title: 'Refocus and Reset',
    durationMinutes: 2,
    description: 'A slightly brighter rhythm for returning to what matters next.',
    openingPrompt: 'Let distractions wait while you follow the next few moments.',
    paceLabel: 'Brisk and energising',
    visualLabel: 'Small, bright focus colours',
    prompts: [
      'Let distractions wait for the next few moments.',
      'Bring your attention back each time it wanders.',
      'Choose one small next step as the rhythm finishes.',
    ],
    settings: {
      orbColor: 'focus', orbSize: 2.1, orbSpacing: 68,
      reappearSpeed: 0.9, fadeDurationMs: 300,
      audioVolume: 45, kidsMode: false,
    },
  },
]);

export const getRoutine = (id) => ROUTINES.find((routine) => routine.id === id) || null;

export const getRoutineProgress = (routine, secondsLeft) => {
  if (!routine || secondsLeft == null) return 0;
  const total = routine.durationMinutes * 60;
  if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(secondsLeft)) return 0;
  return Math.min(1, Math.max(0, (total - secondsLeft) / total));
};

// Most routines keep one fixed visual tempo. A routine may instead define a
// pace journey so its rhythm changes gradually over its elapsed duration. The
// returned values are always safe to pass to the existing orb/timer behavior.
export const getRoutineTiming = (routine, secondsLeft) => {
  const settings = routine?.settings || {};
  const journey = settings.paceJourney;
  const fixedTiming = {
    reappearSpeed: settings.reappearSpeed,
    fadeDurationMs: settings.fadeDurationMs,
  };

  if (!journey) return fixedTiming;

  const progress = getRoutineProgress(routine, secondsLeft);
  const interpolate = (start, end) => start + ((end - start) * progress);

  return {
    reappearSpeed: interpolate(
      journey.startReappearSpeed ?? settings.reappearSpeed,
      journey.endReappearSpeed ?? settings.reappearSpeed
    ),
    fadeDurationMs: Math.round(interpolate(
      journey.startFadeDurationMs ?? settings.fadeDurationMs,
      journey.endFadeDurationMs ?? settings.fadeDurationMs
    )),
  };
};

export const getRoutinePrompt = (routine, secondsLeft) => {
  if (!routine?.prompts?.length || secondsLeft == null) return routine?.openingPrompt || '';
  const elapsedFraction = getRoutineProgress(routine, secondsLeft);
  if (elapsedFraction >= 0.75) return routine.prompts[2];
  if (elapsedFraction >= 0.25) return routine.prompts[1];
  return routine.prompts[0];
};
